import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, BookOpen, ClipboardCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/features/auth/use-auth';
import { ApiRequestError } from '@/shared/api/api-error';
import { useCurrentWorkspace } from '@/shared/api/queries/workspace';
import { waitForWorkspaceOperation } from '@/shared/api/wait-for-workspace-operation';
import { StudioLayout } from '@/shared/layout/studio-layout';
import { cn } from '@/shared/lib/cn';
import { useMediaQuery } from '@/shared/lib/use-media-query';
import { useResizableWidth } from '@/shared/lib/use-resizable-width';
import { activityView } from '@/shared/program/derive';
import { useProgramFull } from '@/shared/program/use-program-full';
import { useRecordStudioVisit } from '@/shared/program/use-studio-resume';
import { useTheme } from '@/shared/theme/use-theme';
import { activityHref } from '@/shared/ui/activity/activity-kind';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { buttonVariants } from '@/shared/ui/button-variants';
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog';
import { ResizeHandle } from '@/shared/ui/resize-handle';
import { LoadingStatus } from '@/shared/ui/spinner';

import { prepareWorkshopSignOut } from '../api/prepare-sign-out';
import { useExplanationDraft } from '../api/use-explanation-draft';
import { useKeepWorking } from '../api/use-keep-working';
import { useRetryWorkshopReview } from '../api/use-retry-workshop-review';
import {
  useEditorConnection,
  useIdeLaunch,
  useWorkshopEvidence,
  useWorkshopSession,
  useWorkshopSolution,
} from '../api/use-workshop-session';
import {
  useWorkshopWorkspaceSwitch,
  WORKSHOP_SWITCH_MUTATION_KEY,
} from '../api/use-workshop-workspace-switch';
import { useWorkspaceCommands } from '../api/use-workspace-commands';
import { WorkshopAssessment } from '../components/workshop-assessment';
import { WorkshopCheckpoint } from '../components/workshop-checkpoint';
import { WorkshopIde } from '../components/workshop-ide';
import { WorkshopInstructions } from '../components/workshop-instructions';
import { WorkshopViewSwitcher } from '../components/workshop-view-switcher';
import '../workshop.css';
import '../workshop-workbench.css';
import { hasRecoveryMessage, workspaceFailureMessage } from '../workspace-feedback';

import type { PendingCommand } from '../api/workspace-session';
import type { WorkshopView } from '../components/workshop-view-switcher';
import type { WorkspaceAvailability } from '../workspace-progress';
import type { SignOutGuard } from '@/features/auth/auth-context';
import type { CurrentWorkspace } from '@mio/shared';
import type { CSSProperties } from 'react';

const VIEW_STORAGE_KEY = 'mio.workshop-view';
function readStoredView(): WorkshopView | null {
  try {
    const value = localStorage.getItem(VIEW_STORAGE_KEY);
    return value === 'studio' || value === 'split' || value === 'code' ? value : null;
  } catch {
    return null;
  }
}
function storeView(view: WorkshopView) {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // A view preference is a convenience only.
  }
}
/** The API refuses Workshop activation while Field holds the shared compute. */
const isFieldHold = (error: unknown) =>
  error instanceof ApiRequestError && error.code === 'workspace_other_domain_active';
const fieldPauseCopy: Record<string, string> = {
  editor_changed:
    'Your Workbench editor is still open in another tab or window. Save and close it there, or wait about two minutes for it to clear, then try again.',
  editor_in_use:
    'Your Workbench editor is still open in another tab or window. Save and close it there, or wait about two minutes for it to clear, then try again.',
  workspace_busy: 'Wait for the current Workbench save to finish, then try again.',
};
function fieldPauseMessage(error: unknown) {
  if (error instanceof ApiRequestError) {
    const copy = fieldPauseCopy[error.code];
    if (copy) return copy;
    if (hasRecoveryMessage(error.code)) return workspaceFailureMessage(error.code);
  }
  return error instanceof Error ? error.message : 'Workbench could not be paused. Try again.';
}

export function WorkshopDetailPage({
  activityId,
  visible = true,
}: {
  activityId: string;
  visible?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { resolved: colorScheme } = useTheme();
  const { user, signingOut, registerSignOutGuard } = useAuth();
  const program = useProgramFull();
  const current = useCurrentWorkspace();
  const workspace = current.data?.workspace ?? null;
  const selectedId = current.data?.activity?.id ?? null;
  const requested = program.data ? activityView(program.data, activityId) : null;
  const selected = program.data && selectedId ? activityView(program.data, selectedId) : null;
  // An owned pinned attempt remains readable after its activity leaves the active
  // curriculum. The server scopes this session to the authenticated learner.
  const requestedSession = useWorkshopSession(
    !requested || requested.kind === 'workshop' ? activityId : null,
  );
  const selectedSession = useWorkshopSession(selectedId);
  const session = selectedId ? selectedSession : requestedSession;
  const retainedRequest = Boolean(
    !requested && requestedSession.data?.attempt && requestedSession.data.task,
  );
  const requestedWorkshop = requested?.kind === 'workshop' || retainedRequest;
  const requestedTitle = requested?.title ?? requestedSession.data?.task?.title;
  const evidence = useWorkshopEvidence(workspace?.activeAttemptId ?? null);
  const submissionPolicy = evidence.data?.policy ?? null;
  const solution = useWorkshopSolution(
    workspace?.activeAttemptId ?? null,
    submissionPolicy?.solutionRevealed ?? false,
  );
  const retryReview = useRetryWorkshopReview();
  const keepWorking = useKeepWorking();
  const fieldSwitch = useWorkshopWorkspaceSwitch();
  const fieldReleases = useIsMutating({ mutationKey: WORKSHOP_SWITCH_MUTATION_KEY });
  const fieldReleasing = fieldSwitch.isPending || fieldReleases > 0;
  const commands = useWorkspaceCommands(current.data);
  const launch = useIdeLaunch(workspace);
  // A failed relaunch must not disown an editor that is still open: keep probing
  // the last posted grant's context until a new grant is actually posted.
  const [retainedLaunch, setRetainedLaunch] = useState<{
    context: string;
    launchId: string;
  } | null>(null);
  const grantedContext = launch.isSuccess ? launch.data.context : null;
  const grantedLaunchId = launch.isSuccess ? launch.data.launchId : null;
  useEffect(() => {
    if (!grantedContext || !grantedLaunchId) return;
    setRetainedLaunch((previous) =>
      previous?.context === grantedContext && previous.launchId === grantedLaunchId
        ? previous
        : { context: grantedContext, launchId: grantedLaunchId },
    );
  }, [grantedContext, grantedLaunchId]);
  const activeLaunch = launch.data ?? retainedLaunch;
  const editorConnection = useEditorConnection(
    workspace,
    activeLaunch?.context ?? null,
    activeLaunch?.launchId,
  );
  const submissionContext = `${workspace?.id}:${workspace?.generation}:${workspace?.activeAttemptId}`;
  // Field holds the shared compute: activation would be refused until Field is released.
  const fieldHoldsCompute = Boolean(workspace?.fieldActive) || isFieldHold(commands.requestError);
  const editorTransportConnected =
    workspace?.state === 'ready' &&
    activeLaunch?.context === submissionContext &&
    !editorConnection.isError &&
    editorConnection.data?.connected === true &&
    // A retained extension host without a browser cannot run the save barrier.
    editorConnection.data.browserAbsent !== true;
  const editorConnected =
    editorTransportConnected && editorConnection.data?.workspaceReady !== false;
  const [view, setViewState] = useState<WorkshopView>(() => readStoredView() ?? 'split');
  // A learner's explicit choice sticks for this browser; otherwise the layout follows the
  // available width and shows the editor once it connects.
  const [viewChosen, setViewChosen] = useState(() => readStoredView() !== null);
  const setView = (next: WorkshopView, chosen = false) => {
    setViewState(next);
    if (chosen) {
      setViewChosen(true);
      storeView(next);
    }
  };
  const narrow = useMediaQuery('(max-width: 1023px)');
  const surface = useRef<HTMLDivElement>(null);
  const codePanel = useRef<HTMLDivElement>(null);
  const viewControls = useRef<HTMLDivElement>(null);
  const restoreViewFocus = useRef(false);
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const splitAvailable = availableWidth === null ? !narrow : availableWidth >= 760;
  const effectiveView = view === 'split' && !splitAvailable ? 'studio' : view;
  useEffect(() => {
    const element = surface.current;
    if (!element) return;
    const update = () => {
      const width = element.getBoundingClientRect().width;
      if (width <= 0) return;
      if (width < 760) {
        const focused = document.activeElement;
        restoreViewFocus.current =
          codePanel.current?.contains(focused) === true ||
          (focused instanceof HTMLElement && focused.dataset.workshopView === 'split');
      }
      setAvailableWidth(width);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (view !== 'split' || splitAvailable) return;
    // A narrower shell can hide the editor even without a click. Restore focus
    // only if it was inside the pane that just disappeared.
    if (restoreViewFocus.current || codePanel.current?.contains(document.activeElement)) {
      viewControls.current
        ?.querySelector<HTMLButtonElement>('[role="radio"][aria-checked="true"]')
        ?.focus();
    }
    restoreViewFocus.current = false;
    setView('studio');
  }, [view, splitAvailable]);
  const autoCodeContext = useRef<string | null>(null);
  useEffect(() => {
    if (!visible || viewChosen || splitAvailable || !editorConnected) return;
    if (autoCodeContext.current === submissionContext) return;
    autoCodeContext.current = submissionContext;
    setViewState('code');
  }, [visible, viewChosen, splitAvailable, editorConnected, submissionContext]);
  const briefPanel = useRef<HTMLDivElement>(null);
  const [briefView, setBriefView] = useState<'instructions' | 'submission'>('instructions');
  // Which brief text to read: the version pinned to the attempt, or the activity's current
  // text. Only local authoring (development builds) may switch; the review stays pinned.
  const [guide, setGuide] = useState(() => new URLSearchParams(location.search).get('guide'));
  const briefWidth = useResizableWidth({
    storageKey: 'mio.workshop-instructions-width',
    defaultWidth: 440,
    min: 340,
    max: 640,
    reserve: 680,
    edge: 'right',
  });
  useEffect(() => setBriefView('instructions'), [selectedId]);
  const [preparing, setPreparing] = useState(false);
  const [preparingAction, setPreparingAction] = useState<PendingCommand['action'] | null>(null);
  const preparingRef = useRef(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // An error belongs to the context it happened in; a new activation or attempt
  // must not keep showing it.
  useEffect(() => setActionError(null), [submissionContext]);
  const [confirmSubmit, setConfirmSubmit] = useState<string | null>(null);
  const [confirmationError, setConfirmationError] = useState<{
    action: 'activate' | 'submit';
    context: string;
    message: string;
  } | null>(null);
  const [acceptedSwitch, setAcceptedSwitch] = useState<string | null>(null);
  // Dismissing the switch prompt means "stay here and read", not "leave".
  const [dismissedSwitch, setDismissedSwitch] = useState<string | null>(null);
  const firstOpen = useRef<string | null>(null);
  const busy = preparing || commands.busy || signingOut || retryReview.isPending;
  const backgroundSaving = Boolean(
    commands.busy &&
    !preparing &&
    !signingOut &&
    !retryReview.isPending &&
    !commands.uncertain &&
    !commands.statusError &&
    workspace?.state === 'ready' &&
    commands.operation?.action === 'checkpoint' &&
    commands.operation.background === true &&
    ['pending', 'running'].includes(commands.operation.status),
  );
  const foregroundBusy = busy && !backgroundSaving;
  const draft = useExplanationDraft(
    workspace?.activeAttemptId ?? null,
    current.data?.draft ?? null,
    foregroundBusy,
    user?.id ?? null,
  );
  const signOutGuard = useRef<SignOutGuard>(() => Promise.resolve());
  signOutGuard.current = (latest) =>
    prepareWorkshopSignOut({
      workspace,
      latest,
      initialized: Boolean(current.data && !current.error && commands.initialized),
      busy: preparing || commands.busy,
      backgroundSaving,
      awaitBackground: () =>
        commands.operation
          ? waitForWorkspaceOperation(commands.operation, { client: queryClient })
          : Promise.resolve(),
      otherUnsavedDrafts: draft.hasOtherUnsavedDrafts,
      flush: draft.flush,
      checkpoint: () => commands.execute('checkpoint'),
    });
  useEffect(
    () => registerSignOutGuard((latest) => signOutGuard.current(latest), 'workshop'),
    [registerSignOutGuard],
  );
  const switchKey = `${workspace?.generation}:${selectedId}:${activityId}`;
  const switching = Boolean(selectedId && selectedId !== activityId && requestedWorkshop);
  const canOpen = Boolean(
    current.data?.enabled &&
    requestedWorkshop &&
    requestedSession.data?.task &&
    requestedSession.data.entitled &&
    (!retainedRequest || requestedSession.data.attempt?.status !== 'abandoned'),
  );
  const availability: WorkspaceAvailability =
    !requestedWorkshop || requestedSession.isPending
      ? 'loading'
      : !requestedSession.data?.task
        ? 'unpublished'
        : !requestedSession.data.entitled
          ? 'restricted'
          : 'openable';
  // The server accepts activation from failed compute: it restores the last checkpoint.
  const canSwitch =
    editorConnected || workspace?.state === 'stopped' || workspace?.state === 'failed';
  // Once accepted, the dialog stays until the selection actually changes, so
  // the learner sees "saving and opening" rather than a page of disabled buttons;
  // a failed switch keeps it open with the reason and a retry.
  const switchInProgress = acceptedSwitch === switchKey && commands.busy;
  const switchFailure =
    acceptedSwitch === switchKey && !commands.busy && commands.operation?.status === 'failed'
      ? workspaceFailureMessage(commands.operation.errorCode)
      : null;
  const switchPrompt =
    visible &&
    switching &&
    canOpen &&
    canSwitch &&
    dismissedSwitch !== switchKey &&
    (acceptedSwitch === switchKey || !commands.busy);

  async function perform(action: PendingCommand['action'], target: string | null = null) {
    if (preparingRef.current) return;
    setConfirmationError(null);
    const reportError = (message: string) => {
      setActionError(message);
      if (action === 'activate' || action === 'submit')
        setConfirmationError({
          action,
          context: action === 'submit' ? submissionContext : switchKey,
          message,
        });
    };
    if (workspace?.state === 'ready' && workspace.activeAttemptId && !editorConnected) {
      reportError('Reconnect the IDE before saving, switching or submitting.');
      return;
    }
    preparingRef.current = true;
    setPreparing(true);
    setPreparingAction(action);
    setActionError(null);
    try {
      await draft.flush();
      await commands.execute(action, target);
      if (action === 'activate') setAcceptedSwitch(switchKey);
      if (action === 'submit') setConfirmSubmit(null);
    } catch (error) {
      // Field holding the compute is shown as its own notice with the way forward.
      if (!isFieldHold(error))
        reportError(
          error instanceof Error ? error.message : 'The workspace operation could not start.',
        );
    } finally {
      preparingRef.current = false;
      setPreparing(false);
      setPreparingAction(null);
    }
  }
  /** Pause Field in place, then let the deliberate-visit logic open this workshop on the same compute. */
  async function openOverField() {
    if (fieldReleasing || preparingRef.current) return;
    setActionError(null);
    setConfirmationError(null);
    try {
      await fieldSwitch.mutateAsync();
    } catch (error) {
      setActionError(fieldPauseMessage(error));
      return;
    }
    firstOpen.current = null;
    await refetchStatus.current().catch(() => undefined);
  }
  const execute = commands.execute;
  const currentContext = useRef(submissionContext);
  currentContext.current = submissionContext;
  const resumeRequest = useRef<string | null>(null);
  const refetchStatus = useRef(current.refetch);
  refetchStatus.current = current.refetch;
  const entryVisit = useRef(0);
  const [entryStatus, setEntryStatus] = useState<{
    visit: number;
    dataUpdatedAt: number;
    data: CurrentWorkspace | undefined;
    failed: boolean;
  } | null>(null);
  useEffect(() => {
    const visit = ++entryVisit.current;
    let cancelled = false;
    firstOpen.current = null;
    setEntryStatus(null);
    if (visible) {
      // A retained page can still cache "ready" after idle pause. A deliberate
      // visit gets one fresh status read before its automatic-open decision.
      void refetchStatus.current().then(
        (result) => {
          if (!cancelled)
            setEntryStatus({
              visit,
              dataUpdatedAt: result.dataUpdatedAt,
              data: result.data,
              failed: result.isError,
            });
        },
        () => undefined,
      );
    }
    return () => {
      cancelled = true;
    };
  }, [visible, activityId]);
  useEffect(() => {
    if (signingOut) ++entryVisit.current;
  }, [signingOut]);
  useEffect(() => {
    if (!visible) {
      firstOpen.current = null;
      resumeRequest.current = null;
      return;
    }
    if (
      document.visibilityState === 'hidden' ||
      entryStatus?.visit !== entryVisit.current ||
      current.dataUpdatedAt < entryStatus.dataUpdatedAt ||
      current.isFetching ||
      current.error ||
      !workspace ||
      !canOpen ||
      !commands.initialized ||
      busy ||
      fieldHoldsCompute ||
      fieldReleasing ||
      firstOpen.current === activityId
    )
      return;
    firstOpen.current = activityId;
    // Consume a successfully observed ready visit even if a subsequent idle poll
    // arrived before this render. Polling alone must never become resume intent.
    const entered = entryStatus.failed ? current.data : entryStatus.data;
    if (
      entered?.activity &&
      (entered.activity.id !== activityId || entered.workspace?.state === 'ready')
    )
      return;
    if (
      selectedId &&
      (selectedId !== activityId || !['stopped', 'failed'].includes(workspace.state))
    )
      return;
    resumeRequest.current = `${submissionContext}:${workspace.updatedAt}`;
    void execute('activate', activityId).catch((error: unknown) => {
      if (isFieldHold(error)) return;
      setActionError(error instanceof Error ? error.message : 'The activity could not open.');
    });
  }, [
    visible,
    selectedId,
    workspace,
    canOpen,
    commands.initialized,
    busy,
    fieldHoldsCompute,
    fieldReleasing,
    activityId,
    execute,
    submissionContext,
    entryStatus,
    current.dataUpdatedAt,
    current.isFetching,
    current.error,
    current.data,
  ]);
  // The route can request B while A remains selected. Only remember B after
  // the saved selection reaches B, never after a cancelled or failed switch.
  const activationUnsettled =
    preparingAction === 'activate' ||
    ((commands.action ?? commands.operation?.action) === 'activate' &&
      (commands.busy ||
        commands.operation?.status === 'failed' ||
        commands.uncertain ||
        Boolean(commands.statusError)));
  const selectedVisit =
    selectedId === activityId &&
    current.data?.activity?.accessible &&
    requestedSession.data?.entitled &&
    requestedSession.data.task?.activityId === activityId &&
    requestedSession.data.attempt?.id === workspace?.activeAttemptId &&
    requestedSession.data.attempt?.status !== 'abandoned';
  const readableOnlyVisit =
    !selectedId &&
    !workspace?.activeAttemptId &&
    requested?.kind === 'workshop' &&
    requestedSession.data?.entitled &&
    (!current.data?.enabled || !requestedSession.data.task);
  useRecordStudioVisit(
    visible &&
      !signingOut &&
      program.data?.slug &&
      !program.error &&
      !current.error &&
      !requestedSession.error &&
      entryStatus?.visit === entryVisit.current &&
      !entryStatus.failed &&
      !activationUnsettled &&
      (selectedVisit || readableOnlyVisit)
      ? { programSlug: program.data.slug, activityId, kind: 'workshop' }
      : null,
    { entryId: activityId, visible },
  );
  async function refreshIde() {
    if (!workspace || busy || preparingRef.current) return;
    if (workspace.state !== 'ready') {
      await perform('activate', selectedId ?? activityId);
      return;
    }
    preparingRef.current = true;
    setPreparing(true);
    setPreparingAction('checkpoint');
    setActionError(null);
    const context = submissionContext;
    try {
      await draft.flush();
      // A live extension may have loaded the wrong folder. It still owns buffers
      // that must pass the save barrier before refreshing the browser.
      if (editorTransportConnected) {
        const saved = await waitForWorkspaceOperation(await execute('checkpoint'), {
          client: queryClient,
        });
        if (saved.status !== 'succeeded') throw new Error(workspaceFailureMessage(saved.errorCode));
      }
      if (currentContext.current !== context)
        throw new Error('The active workshop changed. Refresh its IDE again.');
      await launch.mutateAsync();
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : 'Could not refresh the IDE. Your workspace has not been reset.',
      );
    } finally {
      preparingRef.current = false;
      setPreparing(false);
      setPreparingAction(null);
    }
  }
  function continueSelected() {
    if (!visible) return;
    setActionError(null);
    setConfirmationError(null);
    if (selectedId)
      void navigate({
        to: '/workshop/$activityId',
        params: { activityId: selectedId },
        replace: true,
      });
  }
  // While another workshop stays selected, a requested activity that cannot open
  // is still readable: show its brief instead of the selected one.
  const readOnlyRequest = switching && !canOpen;
  // Never fill missing selected metadata with another activity's summary/navigation.
  const activity = readOnlyRequest ? requested : selectedId ? selected : requested;
  const task = readOnlyRequest ? requestedSession.data?.task : session.data?.task;
  // Local authoring can preview revised prose without repinning an existing attempt.
  // The assessment continues to receive the unchanged task below.
  const revisedSinceStart =
    import.meta.env.DEV &&
    activity?.kind === 'workshop' &&
    Boolean(task) &&
    activity.body !== task?.body;
  const previewLatestGuide =
    import.meta.env.DEV && guide === 'latest' && activity?.kind === 'workshop' && Boolean(task);
  const switchGuide = (next: 'latest' | 'pinned') => {
    const url = new URL(location.href);
    url.searchParams.set('guide', next);
    history.replaceState(history.state, '', url);
    setGuide(next);
  };
  const brief =
    activity && task
      ? {
          ...activity,
          title: task.title,
          body: previewLatestGuide ? activity.body : task.body,
          acceptance: task.acceptance,
        }
      : activity;
  const readyToRead =
    brief?.kind === 'workshop' || Boolean(task && (selectedId !== null || retainedRequest));
  const showWorkspace = readyToRead || Boolean(workspace?.activeAttemptId);
  const error =
    actionError ??
    (isFieldHold(commands.requestError) ? undefined : commands.requestError?.message);
  const switchError =
    confirmationError?.action === 'activate' && confirmationError.context === switchKey
      ? confirmationError.message
      : null;
  const submissionError =
    confirmationError?.action === 'submit' && confirmationError.context === submissionContext
      ? confirmationError.message
      : null;
  const visibleActivityId = readOnlyRequest ? activityId : (selectedId ?? activityId);
  const visibleCode =
    activity?.code ??
    (current.data?.activity?.id === visibleActivityId ? current.data.activity.code : '');
  const mioActivity = readyToRead
    ? {
        id: visibleActivityId,
        code: visibleCode,
        kind: 'workshop' as const,
        title: task?.title ?? activity?.title ?? current.data?.activity?.title ?? 'Workshop',
        status: activity?.status ?? 'in_progress',
      }
    : null;
  const content = (
    <>
      <div
        ref={surface}
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col"
        data-mio-workshop-surface={
          visible && mioActivity
            ? JSON.stringify({
                activity: mioActivity,
                ...(switching
                  ? {
                      transition: {
                        requestedActivityId: activityId,
                        state: readOnlyRequest ? 'preview' : 'pending',
                      },
                    }
                  : {}),
              })
            : undefined
        }
      >
        <div
          className="max-h-[25dvh] shrink-0 overflow-y-auto px-4 text-sm [&:not(:empty)]:py-2"
          aria-label="Workshop status"
        >
          {current.error ? (
            <div className="my-2 space-y-2">
              <Alert>
                Could not {current.data ? 'refresh' : 'load'} your workspace status. Your editor and
                saved work remain in place.
              </Alert>
              <Button
                variant="secondary"
                size="sm"
                loading={current.isFetching}
                loadingLabel="Checking workspace…"
                onClick={() => void current.refetch()}
              >
                Retry workspace status
              </Button>
            </div>
          ) : null}
          {program.isPending || current.isPending ? (
            <LoadingStatus label="Loading activity…" />
          ) : null}
          {program.error ? (
            <div className="my-2 space-y-2">
              <Alert>
                Could not {program.data ? 'refresh' : 'load'} the workshop instructions.
              </Alert>
              <Button
                variant="secondary"
                size="sm"
                loading={program.isFetching}
                loadingLabel="Loading instructions…"
                onClick={() => void program.refetch()}
              >
                Retry instructions
              </Button>
            </div>
          ) : null}
          {!program.isPending &&
          !program.error &&
          !requestedSession.isPending &&
          !requestedWorkshop ? (
            <div className="my-2 space-y-3">
              <Alert>This activity is no longer available. Choose a workshop from Studio.</Alert>
              <Link to="/studio" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
                Browse workshops
              </Link>
            </div>
          ) : null}
          {session.error ? (
            <div className="my-2 space-y-2">
              <Alert>
                Could not {session.data ? 'refresh' : 'load'} this workshop’s access and task
                details.
              </Alert>
              <Button
                variant="secondary"
                size="sm"
                loading={session.isFetching}
                loadingLabel="Checking workshop…"
                onClick={() => void session.refetch()}
              >
                Retry workshop details
              </Button>
            </div>
          ) : null}
          {requestedWorkshop && session.isPending && !session.error ? (
            <LoadingStatus label="Loading workshop details…" />
          ) : null}
          {fieldHoldsCompute && canOpen && !signingOut ? (
            <div
              className="border-line bg-sidebar my-2 space-y-3 rounded-lg border p-4 text-sm leading-6"
              role="status"
            >
              <p>
                Workbench is using your coding environment. Save Workbench and open this workshop
                here; Workbench resumes later where you left off.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  loading={fieldReleasing}
                  loadingLabel="Saving Workbench and opening the workshop…"
                  disabled={busy || fieldReleasing}
                  onClick={() => {
                    void openOverField();
                  }}
                >
                  Save Workbench and open Workshop
                </Button>
                <Link to="/field" className="text-link text-xs font-medium">
                  Open Workbench instead
                </Link>
              </div>
            </div>
          ) : null}
          {error ? <Alert>{error}</Alert> : null}
          {signingOut ? <LoadingStatus label="Signing out…" /> : null}
          {draft.hasOtherUnsavedDrafts ? (
            <Alert>
              An explanation in another activity still has unsaved changes. Return to that activity
              to save it before closing this tab.
            </Alert>
          ) : null}
          {commands.uncertain ? (
            <div role="alert" className="mb-4 space-y-2">
              <p>
                The request may have reached the server. Retry the same request to find its result.
              </p>
              <Button
                disabled={preparing}
                onClick={() => {
                  setActionError(null);
                  void commands.retryRequest().catch(() => undefined);
                }}
              >
                Retry request
              </Button>
            </div>
          ) : null}
          {commands.statusError ? (
            <div role="alert" className="mb-4 space-y-2">
              <p>
                Could not check the operation. Your editor stays locked until its result is known.
              </p>
              <Button
                onClick={() => {
                  void commands.retryStatus();
                }}
              >
                Check operation
              </Button>
            </div>
          ) : null}
          {commands.operation?.status === 'failed' &&
          // A background backup or idle sweep that failed is retried by the next sweep;
          // only the learner's own command needs their attention here.
          commands.operation.maintenance !== true &&
          error !== workspaceFailureMessage(commands.operation.errorCode) ? (
            <Alert>{workspaceFailureMessage(commands.operation.errorCode)}</Alert>
          ) : null}
          {switching ? (
            <div className="space-y-2">
              <p>
                {readOnlyRequest
                  ? availability === 'unpublished'
                    ? 'This workshop has no coding environment yet. You can read its brief here; your current workshop stays open.'
                    : availability === 'restricted'
                      ? 'Your enrollment does not allow this workshop’s coding environment. You can read its brief here; your current workshop stays open.'
                      : 'Checking whether this workshop can open. Your current workshop stays open.'
                  : 'Your current activity remains open until its progress is saved.'}
              </p>
              {!canSwitch && canOpen ? (
                <LoadingStatus
                  label={
                    workspace?.state === 'stopping'
                      ? 'Waiting for your current workspace to finish pausing…'
                      : workspace?.state === 'starting'
                        ? 'Starting your current workspace so it can be saved first…'
                        : 'Connecting to the current IDE before saving it…'
                  }
                />
              ) : null}
              <Button
                disabled={busy || !canOpen || !canSwitch}
                onClick={() => {
                  setActionError(null);
                  setConfirmationError(null);
                  setAcceptedSwitch(null);
                  setDismissedSwitch(null);
                }}
              >
                Switch to {requestedTitle}
              </Button>
              <Button
                variant="secondary"
                data-workshop-navigation
                disabled={busy}
                onClick={continueSelected}
              >
                Continue {current.data?.activity?.title}
              </Button>
            </div>
          ) : null}
          {showWorkspace && effectiveView === 'studio' && !fieldHoldsCompute ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              aria-controls="workshop-code-pane"
              onClick={() => {
                setView('code', true);
                requestAnimationFrame(() => codePanel.current?.focus({ preventScroll: true }));
              }}
            >
              Open Code view
            </Button>
          ) : null}
        </div>
        {showWorkspace ? (
          <div
            className="workshop-workbench"
            data-view={effectiveView}
            style={{ '--brief-width': `${briefWidth.width}px` } as CSSProperties}
          >
            <div
              id="workshop-brief"
              ref={briefPanel}
              tabIndex={0}
              role="region"
              aria-label="Workshop instructions"
              hidden={effectiveView === 'code'}
              inert={effectiveView === 'code'}
              className={cn(
                'bg-canvas relative min-h-0 min-w-0 flex-col outline-offset-[-2px]',
                effectiveView === 'code' ? 'hidden' : 'flex',
              )}
            >
              {effectiveView === 'split' ? (
                <ResizeHandle
                  resizable={briefWidth}
                  label="Resize instructions"
                  className="-right-3 left-auto"
                />
              ) : null}
              <div
                role="tablist"
                aria-label="Workshop panels"
                className="border-line flex h-12 shrink-0 gap-5 border-b px-5"
              >
                {(['instructions', 'submission'] as const).map((view) => (
                  <button
                    key={view}
                    id={`workshop-tab-${view}`}
                    type="button"
                    role="tab"
                    aria-selected={briefView === view}
                    aria-controls={`workshop-panel-${view}`}
                    tabIndex={briefView === view ? 0 : -1}
                    onClick={() => setBriefView(view)}
                    onKeyDown={(event) => {
                      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                      event.preventDefault();
                      const next =
                        event.key === 'Home'
                          ? 'instructions'
                          : event.key === 'End'
                            ? 'submission'
                            : view === 'instructions'
                              ? 'submission'
                              : 'instructions';
                      setBriefView(next);
                      document
                        .getElementById(`workshop-tab-${next}`)
                        ?.focus({ preventScroll: true });
                    }}
                    className={cn(
                      'focus-visible:ring-ring -mb-px inline-flex items-center gap-2 border-b-2 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset',
                      briefView === view
                        ? 'border-fg text-fg'
                        : 'text-fg-muted hover:text-fg border-transparent',
                    )}
                  >
                    {view === 'instructions' ? (
                      <BookOpen className="size-3.5" aria-hidden="true" />
                    ) : (
                      <ClipboardCheck className="size-3.5" aria-hidden="true" />
                    )}
                    {view === 'instructions' ? 'Instructions' : 'Submission'}
                  </button>
                ))}
              </div>
              <div
                id="workshop-panel-instructions"
                role="tabpanel"
                aria-labelledby="workshop-tab-instructions"
                hidden={briefView !== 'instructions'}
                className={cn(
                  'min-h-0 flex-1 flex-col',
                  briefView === 'instructions' ? 'flex' : 'hidden',
                )}
              >
                {previewLatestGuide || revisedSinceStart ? (
                  <div className="shrink-0 px-5 pt-4">
                    <Alert tone="info">
                      {previewLatestGuide
                        ? 'Previewing the latest instructions. Your saved project and review version are unchanged. '
                        : 'These instructions were revised after this attempt started. You are reading the version pinned to it. '}
                      <button
                        type="button"
                        className="font-medium underline underline-offset-2"
                        onClick={() => switchGuide(previewLatestGuide ? 'pinned' : 'latest')}
                      >
                        {previewLatestGuide
                          ? 'Back to the pinned version'
                          : 'Read the current instructions'}
                      </button>
                    </Alert>
                  </div>
                ) : null}
                {brief ? (
                  <WorkshopInstructions
                    title={brief.title}
                    body={brief.body}
                    progressScope={brief.id}
                    acceptance={brief.acceptance}
                    summary={brief.summary}
                    estimatedMinutes={brief.estimatedMinutes}
                    onReview={() => {
                      setBriefView('submission');
                      document
                        .getElementById('workshop-tab-submission')
                        ?.focus({ preventScroll: true });
                    }}
                  />
                ) : task ? (
                  <WorkshopInstructions
                    title={task.title}
                    body={task.body}
                    acceptance={task.acceptance}
                    onReview={() => {
                      setBriefView('submission');
                      document
                        .getElementById('workshop-tab-submission')
                        ?.focus({ preventScroll: true });
                    }}
                  />
                ) : null}
              </div>
              <div
                id="workshop-panel-submission"
                data-scroll-pane="workshop-submission"
                role="tabpanel"
                aria-labelledby="workshop-tab-submission"
                hidden={briefView !== 'submission'}
                className={cn(
                  'min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-6',
                  briefView !== 'submission' && 'hidden',
                )}
              >
                {!session.isPending && !task ? (
                  <p className="text-fg-muted text-sm">
                    The task-specific coding environment for this activity has not been published
                    yet.
                  </p>
                ) : null}
                {workspace?.activeAttemptId ? (
                  <WorkshopAssessment
                    task={task ?? null}
                    workspace={workspace}
                    evidence={evidence}
                    draft={draft}
                    busy={busy || !current.data?.enabled || switching}
                    draftBlocked={foregroundBusy || !current.data?.enabled || switching}
                    editorConnected={editorConnected}
                    blockedReason={
                      switching
                        ? 'Finish switching workshops before submitting.'
                        : !current.data?.enabled
                          ? 'Coding is currently unavailable. Your explanation and previous submissions are kept here.'
                          : null
                    }
                    solution={solution}
                    pendingSubmissionId={
                      commands.operation?.action === 'submit' &&
                      commands.operation.status === 'succeeded'
                        ? commands.operation.submissionId
                        : null
                    }
                    submissionError={submissionError}
                    onSubmit={() => {
                      setActionError(null);
                      setConfirmationError(null);
                      setConfirmSubmit(submissionContext);
                    }}
                    reviewRetry={{
                      pending: retryReview.isPending,
                      error:
                        retryReview.variables?.attemptId === workspace.activeAttemptId
                          ? (retryReview.error?.message ?? null)
                          : null,
                      run: (submissionId) => {
                        if (workspace.activeAttemptId)
                          retryReview.mutate({
                            attemptId: workspace.activeAttemptId,
                            submissionId,
                          });
                      },
                    }}
                    onRetrySave={() => {
                      void draft
                        .flush()
                        .catch((error: unknown) =>
                          setActionError(error instanceof Error ? error.message : 'Saving failed.'),
                        );
                    }}
                  />
                ) : null}
                <details className="border-line text-fg-muted mt-6 border-t pt-4 text-xs">
                  <summary className="mb-3 cursor-pointer">
                    Automatic saves and file coverage
                  </summary>
                  <p className="mb-3">
                    Named text files auto-save in the IDE by default. Moving between app sections
                    keeps your IDE running and autosave active. Source backups run about every five
                    minutes. Files and your explanation are saved before switching workshop
                    activities, submitting or signing out.
                  </p>
                  <p className="mb-3">
                    After 30 minutes without editor interaction, the environment saves and pauses on
                    its own. You get a warning five minutes before, and Resume workspace brings it
                    back with your files.
                  </p>
                  <WorkshopCheckpoint
                    checkpoint={current.data?.checkpoint ?? null}
                    operation={commands.operation ?? null}
                    attemptId={workspace?.activeAttemptId ?? null}
                  />
                </details>
              </div>
              {activity?.previous || activity?.next ? (
                <nav
                  aria-label="Adjacent activities"
                  className="border-line flex h-11 shrink-0 items-center justify-between gap-3 border-t px-5 text-xs"
                >
                  {activity.previous ? (
                    <Link
                      to={activityHref(activity.previous.kind, activity.previous.id)}
                      title={activity.previous.title}
                      className="text-fg-muted hover:text-fg inline-flex items-center gap-1.5"
                    >
                      <ArrowLeft className="size-3.5" aria-hidden="true" />
                      Previous {activity.previous.kind === 'lesson' ? 'lesson' : 'activity'}
                    </Link>
                  ) : (
                    <span />
                  )}
                  {activity.next ? (
                    <Link
                      to={activityHref(activity.next.kind, activity.next.id)}
                      title={activity.next.title}
                      className="text-fg-muted hover:text-fg inline-flex items-center gap-1.5"
                    >
                      Next activity
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  ) : null}
                </nav>
              ) : null}
            </div>
            <div
              id="workshop-code-pane"
              ref={codePanel}
              tabIndex={-1}
              hidden={effectiveView === 'studio'}
              inert={effectiveView === 'studio'}
              className={cn(
                'h-full min-h-0 min-w-0 outline-offset-[-2px]',
                effectiveView === 'studio' && 'hidden',
                briefWidth.dragging && 'pointer-events-none',
              )}
              // Mio reads these identifiers from the DOM; only the visible page and its
              // active attempt publish them, and the server authorizes them itself.
              {...(visible && workspace?.activeAttemptId && selectedId
                ? {
                    'data-mio-ide-workspace': workspace.id,
                    'data-mio-ide-attempt': workspace.activeAttemptId,
                    'data-mio-ide-activity': selectedId,
                    'data-mio-ide-generation': String(workspace.generation),
                  }
                : {})}
            >
              <WorkshopIde
                workspace={workspace}
                colorScheme={colorScheme}
                operation={preparing && !commands.busy ? null : commands.operation}
                action={preparing && !commands.busy ? preparingAction : commands.action}
                launch={launch}
                visible={visible}
                busy={busy}
                backgroundSaving={backgroundSaving}
                enabled={current.data?.enabled ?? true}
                editorConnected={editorConnected}
                editorTransportConnected={editorTransportConnected}
                connectionError={editorConnection.error ?? null}
                hasAppPreview={/^(?:W-FDE(?:1\.[234]|2\.[1-5])|W-CB[45]\.1|W-CB-M2\.[1-6]|W-CB-M3\.[24]|W-CB-M4\.[1-6])$/.test(
                  visibleCode,
                )}
                onRefresh={() => {
                  void refreshIde();
                }}
                onReconnect={() => setActionError(null)}
                onCheckStatus={() => {
                  void current.refetch();
                  void commands.retryStatus();
                }}
                availability={availability}
                idleStopAt={workspace?.idleStopAt ?? null}
                onKeepWorking={() => keepWorking.mutate()}
                keepingWorking={keepWorking.isPending}
                backedUpAt={current.data?.checkpoint?.capturedAt ?? null}
              />
            </div>
          </div>
        ) : null}
      </div>
      <ConfirmationDialog
        open={switchPrompt}
        title={
          switchInProgress
            ? `Saving ${current.data?.activity?.title ?? 'your current activity'} and opening ${requestedTitle ?? 'the other activity'}…`
            : `Switch to ${requestedTitle ?? 'another activity'}?`
        }
        confirmLabel={switchFailure ? 'Try again' : 'Save and switch'}
        cancelLabel="Not now"
        busy={preparing || switchInProgress}
        onConfirm={() => {
          void perform('activate', activityId);
        }}
        onCancel={() => {
          setConfirmationError(null);
          setDismissedSwitch(switchKey);
        }}
      >
        <p>
          {switchInProgress
            ? 'Your files and explanation are being saved before the other activity opens. This usually takes under a minute.'
            : 'We will save your current files and explanation before opening the other activity. You can resume this work later.'}
        </p>
        {(switchError ?? switchFailure) ? <p role="alert">{switchError ?? switchFailure}</p> : null}
      </ConfirmationDialog>
      <ConfirmationDialog
        open={visible && confirmSubmit === submissionContext && !switching}
        title="Submit your code for review?"
        confirmLabel="Confirm submission"
        cancelLabel="Continue coding"
        busy={preparing}
        confirmDisabled={!editorConnected || commands.busy}
        onConfirm={() => {
          void perform('submit');
        }}
        onCancel={() => {
          setConfirmSubmit(null);
          setConfirmationError(null);
        }}
      >
        <p>
          Your editor files and explanation will be saved as a fixed submission. Mio will review
          them against the problem and report what you completed, what is not done yet, gaps in
          understanding and what to do next.
        </p>
        {submissionPolicy ? (
          <p className="text-fg mt-2 font-medium">
            {submissionPolicy.remaining <= 1
              ? `This is your last submission (${Math.min(submissionPolicy.used + 1, submissionPolicy.limit)} of ${submissionPolicy.limit}). After this review the reference solution is shown and submitting is disabled.`
              : `This is submission ${Math.min(submissionPolicy.used + 1, submissionPolicy.limit)} of ${submissionPolicy.limit}.`}
          </p>
        ) : null}
        {!editorConnected ? (
          <p className="mt-2">Reconnect the IDE before confirming so your files can be saved.</p>
        ) : commands.busy ? (
          <p className="mt-2">Wait for the current workspace operation to finish.</p>
        ) : null}
        {submissionError ? <p role="alert">{submissionError}</p> : null}
      </ConfirmationDialog>
    </>
  );
  return (
    <StudioLayout
      activeId={switching ? selectedId : activityId}
      contentMode="workspace"
      preserveContent
      visible={visible}
      context={
        <span className="truncate text-sm font-medium" title={brief?.title ?? task?.title}>
          {visibleCode ? <span className="text-fg-muted mr-2 text-xs">{visibleCode}</span> : null}
          {brief?.title ?? task?.title ?? 'Workshop'}
        </span>
      }
      toolbar={
        <div ref={viewControls}>
          <WorkshopViewSwitcher
            value={effectiveView}
            splitAvailable={splitAvailable}
            onChange={(next) => setView(next, true)}
          />
        </div>
      }
      headerActions={<></>}
      content={content}
    />
  );
}
